import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { CATEGORIES } from "@/lib/brand";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";
import MoonAvatar from "@/components/MoonAvatar";
import StarLoader from "@/components/StarLoader";
import { useColorSwatches, swatchStyle } from "@/lib/colorSwatches";

const SORTS = [
  { key: "new",   label: "Newest" },
  { key: "asc",   label: "Price: Low to High" },
  { key: "desc",  label: "Price: High to Low" },
];

// Small visual mapping for color swatches — merged with admin-managed hex codes at runtime.
// Names known ahead of time; admin can override or add new colors via the Color Swatches admin panel.
const KNOWN_COLOR_NAMES_FOR_SEARCH = [
  "gold", "rose-gold", "rose", "silver", "platinum", "black", "white", "ivory",
  "pearl", "copper", "bronze", "green", "emerald", "ruby", "red", "blue", "navy", "turquoise",
];

function extractQueryColor(q) {
  if (!q) return null;
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  for (const w of words) {
    const base = w.replace(/s$/, "");
    if (KNOWN_COLOR_NAMES_FOR_SEARCH.includes(w)) return w;
    if (KNOWN_COLOR_NAMES_FOR_SEARCH.includes(base)) return base;
  }
  return null;
}

/**
 * Expand each product into ONE card per unique color variant it has photographed.
 * A product with no explicit color tags produces a single card (variantImageIndex=0).
 * Cards are then INTERLEAVED (round-robin per product) so variants of the same
 * product never appear side-by-side.
 *
 * If `queryColor` is provided, the variant matching that color is bubbled to the
 * front of the product's variant list so the shop grid opens on that variant.
 */
function expandVariants(products, queryColor = null) {
  const perProductCards = products.map((p) => {
    const imgs = p.images || [];
    const cols = p.image_colors || [];
    // Build a map of first-seen color -> image index
    const seen = new Map();
    imgs.forEach((_, i) => {
      const c = (cols[i] || "").trim();
      const key = c.toLowerCase();
      if (!key || seen.has(key)) return;
      seen.set(key, { color: c, imageIndex: i });
    });

    let cards;
    if (seen.size === 0) {
      cards = [{ product: p, variantImageIndex: 0, variantLabel: null }];
    } else {
      cards = [...seen.values()].map((v) => ({
        product: p,
        variantImageIndex: v.imageIndex,
        variantLabel: v.color,
      }));
    }

    // If a color is being searched, move that variant to the front for this product.
    if (queryColor) {
      const qk = queryColor.toLowerCase();
      const idx = cards.findIndex(
        (c) => (c.variantLabel || "").toLowerCase() === qk
      );
      if (idx > 0) {
        const [hit] = cards.splice(idx, 1);
        cards.unshift(hit);
      }
    }
    return cards;
  });

  // Round-robin interleave — pull one card from each product, then loop, until all consumed.
  const out = [];
  let added = true;
  let round = 0;
  while (added) {
    added = false;
    for (const arr of perProductCards) {
      if (round < arr.length) {
        out.push(arr[round]);
        added = true;
      }
    }
    round += 1;
  }
  return out;
}

// Detect a color keyword in a search query. Returns the matched color name or null.
// (Kept as a duplicate declaration guard — the primary one lives above.)
// eslint-disable-next-line no-redeclare
/** Left-side / bottom-sheet filter panel — Category / Price / Color / Availability / Occasion. */
function FiltersPanel({
  categoriesSel, onToggleCategory,
  priceCap, maxPrice, onPriceChange,
  colorsSel, allColors, onToggleColor,
  availability, onAvailabilityChange,
  occasionsSel, allOccasions, onToggleOccasion,
  onReset,
  onClose,
  showCloseButton = false,
  swatchMap,
}) {
  return (
    <div className="space-y-6" data-testid="filters-panel">
      {showCloseButton && (
        <div className="flex items-center justify-between pb-3 border-b border-sj-ink/10">
          <div className="font-display text-lg">Filters</div>
          <button type="button" onClick={onClose} className="p-2 -mr-2 text-sj-inkSoft" aria-label="Close filters" data-testid="filters-close">
            <X size={18} />
          </button>
        </div>
      )}

      {/* Category */}
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-sj-inkSoft mb-3">Category</div>
        <div className="space-y-2">
          {CATEGORIES.map((c) => (
            <label key={c.key} className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`filter-cat-check-${c.key}`}>
              <input
                type="checkbox"
                checked={categoriesSel.includes(c.key)}
                onChange={() => onToggleCategory(c.key)}
                className="accent-sj-goldDark h-4 w-4"
              />
              <span className="text-sj-ink">{c.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Price */}
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-sj-inkSoft mb-3">Price</div>
        <div className="text-xs text-sj-inkSoft mb-2">Up to ₹{priceCap.toLocaleString("en-IN")}</div>
        <input
          type="range"
          min={100}
          max={Math.max(maxPrice, 500)}
          step={50}
          value={priceCap}
          onChange={(e) => onPriceChange(Number(e.target.value))}
          className="w-full accent-sj-gold"
          data-testid="filter-price-slider"
        />
        <div className="mt-1 flex items-center justify-between text-[11px] text-sj-muted">
          <span>₹100</span>
          <span>₹{maxPrice.toLocaleString("en-IN")}</span>
        </div>
      </div>

      {/* Color */}
      {allColors.length > 0 && (
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-sj-inkSoft mb-3">Color</div>
          <div className="flex flex-wrap gap-2">
            {allColors.map((c) => {
              const on = colorsSel.map((x) => x.toLowerCase()).includes(c.toLowerCase());
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => onToggleColor(c)}
                  className={`h-8 pl-1.5 pr-3 rounded-full text-xs border inline-flex items-center gap-2 transition-colors ${on ? "border-sj-gold ring-2 ring-sj-gold/40 bg-sj-cream/60" : "border-sj-ink/15 hover:border-sj-goldDark"}`}
                  data-testid={`filter-color-${c.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span
                    className="h-5 w-5 rounded-full border border-sj-ink/10"
                    style={swatchStyle(c, swatchMap)}
                    aria-hidden="true"
                  />
                  <span className="capitalize">{c}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Availability */}
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-sj-inkSoft mb-3">Availability</div>
        <div className="space-y-2">
          {[
            { key: "all", label: "Show all" },
            { key: "in", label: "In stock only" },
            { key: "low", label: "Few pieces left" },
          ].map((opt) => (
            <label key={opt.key} className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`filter-avail-${opt.key}`}>
              <input
                type="radio"
                name="availability"
                checked={availability === opt.key}
                onChange={() => onAvailabilityChange(opt.key)}
                className="accent-sj-goldDark h-4 w-4"
              />
              <span className="text-sj-ink">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Occasion */}
      {allOccasions.length > 0 && (
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-sj-inkSoft mb-3">Occasion</div>
          <div className="space-y-2">
            {allOccasions.map((o) => (
              <label key={o.id} className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`filter-occasion-${o.id}`}>
                <input
                  type="checkbox"
                  checked={occasionsSel.includes(o.id)}
                  onChange={() => onToggleOccasion(o.id)}
                  className="accent-sj-goldDark h-4 w-4"
                />
                <span className="text-sj-ink">{o.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onReset}
        className="w-full h-10 rounded-full border border-sj-ink/15 text-sm text-sj-inkSoft hover:border-sj-goldDark hover:text-sj-goldDark transition-colors"
        data-testid="filter-reset"
      >
        Reset all filters
      </button>
    </div>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const q        = params.get("q") || "";
  const collection = params.get("collection") || "";
  const swatchMap = useColorSwatches();

  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [maxPrice, setMaxPrice] = useState(3000);
  const [priceCap, setPriceCap] = useState(3000);
  const [sort, setSort]         = useState("new");
  const [showFilters, setShowFilters] = useState(false);

  // Close the mobile filter bottom sheet when the user hits Escape — matches
  // the fullscreen viewer's Esc-to-close convention.
  useEffect(() => {
    if (!showFilters) return;
    const onKey = (e) => { if (e.key === "Escape") setShowFilters(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showFilters]);
  const [searchLocal, setSearchLocal] = useState(q);

  // Advanced filter state (client-side)
  const [categoriesSel, setCategoriesSel] = useState([]); // ['earrings', ...]
  const [colorsSel, setColorsSel] = useState([]);
  const [availability, setAvailability] = useState("all"); // 'all' | 'in' | 'low'
  const [occasionsSel, setOccasionsSel] = useState([]);

  // Recommendations for empty-search state (Moon's fallback picks)
  const [recommended, setRecommended] = useState([]);

  useEffect(() => { setSearchLocal(q); }, [q]);

  // Sync category pill from URL into filter checkboxes so both stay consistent.
  useEffect(() => {
    if (category && category !== "all") setCategoriesSel([category]);
    else setCategoriesSel([]);
  }, [category]);

  useEffect(() => {
    api.get("/collections").then(({ data }) => setCollections(data)).catch(() => {});
    api.get("/occasions").then(({ data }) => setOccasions(data || [])).catch(() => {});
    api.get("/products", { params: { featured: true } })
      .then(({ data }) => setRecommended(data.slice(0, 4)))
      .catch(() => {});
  }, []);

  // Fetch products — keep server-side filters minimal; do the rich filtering
  // client-side so the sidebar can react instantly.
  useEffect(() => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (collection) p.collection = collection;
    api.get("/products", { params: p })
      .then(({ data }) => {
        setProducts(data);
        const highest = data.reduce((m, x) => (x.price > m ? x.price : m), 0);
        const cap = Math.max(500, Math.ceil(highest / 100) * 100);
        setMaxPrice(cap);
        // Reset priceCap to maxPrice on new fetch so nothing is hidden by an old value.
        setPriceCap(cap);
      })
      .finally(() => setLoading(false));
  }, [q, collection]);

  // All colors that appear anywhere in the current fetched products (unique, case-insensitive).
  const allColors = useMemo(() => {
    const seen = new Map();
    for (const p of products) {
      (p.image_colors || []).forEach((c) => {
        const t = (c || "").trim();
        if (!t) return;
        const k = t.toLowerCase();
        if (!seen.has(k)) seen.set(k, t);
      });
    }
    return [...seen.values()];
  }, [products]);

  // Detect any color keyword inside the search query.
  const queryColor = useMemo(() => extractQueryColor(q), [q]);

  // Filtered + expanded variant cards.
  const cards = useMemo(() => {
    let arr = [...products];

    // Category (multi)
    if (categoriesSel.length > 0) {
      arr = arr.filter((p) => categoriesSel.includes(p.category));
    }

    // Price cap
    arr = arr.filter((p) => Number(p.price) <= priceCap);

    // Availability
    if (availability === "in") arr = arr.filter((p) => !p.out_of_stock);
    if (availability === "low") arr = arr.filter((p) => !!p.low_stock);

    // Occasions
    if (occasionsSel.length > 0) {
      arr = arr.filter((p) => (p.occasions || []).some((o) => occasionsSel.includes(o)));
    }

    // Sort by price if requested — done BEFORE expanding to preserve product order.
    if (sort === "asc")  arr.sort((a, b) => a.price - b.price);
    if (sort === "desc") arr.sort((a, b) => b.price - a.price);

    // Expand into per-color variant cards, interleaved so duplicates don't clump.
    let expanded = expandVariants(arr, queryColor);

    // Color filter — keep only cards whose variant matches (case-insensitive).
    if (colorsSel.length > 0) {
      const setLc = new Set(colorsSel.map((c) => c.toLowerCase()));
      expanded = expanded.filter((c) => c.variantLabel && setLc.has(c.variantLabel.toLowerCase()));
    }

    return expanded;
  }, [products, categoriesSel, priceCap, availability, occasionsSel, sort, queryColor, colorsSel]);

  const toggleCategory = (key) => {
    setCategoriesSel((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
    // Also keep URL param in sync (helpful for shareable URLs & category SEO)
    const nxt = new URLSearchParams(params);
    // If exactly one category will be selected, set param; otherwise clear it.
    const nextSel = categoriesSel.includes(key)
      ? categoriesSel.filter((k) => k !== key)
      : [...categoriesSel, key];
    if (nextSel.length === 1) nxt.set("category", nextSel[0]);
    else nxt.delete("category");
    setParams(nxt);
  };

  const toggleColor = (c) => {
    setColorsSel((prev) => {
      const lower = prev.map((x) => x.toLowerCase());
      const idx = lower.indexOf(c.toLowerCase());
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [...prev, c];
    });
  };

  const toggleOccasion = (id) => {
    setOccasionsSel((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const resetAll = () => {
    setCategoriesSel([]);
    setColorsSel([]);
    setAvailability("all");
    setOccasionsSel([]);
    setPriceCap(maxPrice);
    const nxt = new URLSearchParams(params);
    nxt.delete("category");
    setParams(nxt);
  };

  const applySearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (searchLocal.trim()) next.set("q", searchLocal.trim());
    else next.delete("q");
    setParams(next);
  };

  const activeFilterCount =
    categoriesSel.length +
    colorsSel.length +
    occasionsSel.length +
    (availability !== "all" ? 1 : 0) +
    (priceCap < maxPrice ? 1 : 0);

  return (
    <main className="mx-auto max-w-[1440px] px-5 sm:px-10 py-14 sm:py-20" data-testid="shop-page">
      <SEO
        title={category && category !== "all" ? `${category.charAt(0).toUpperCase() + category.slice(1)} — Star Jewels Fashion Jewellery` : "Shop All Artificial Jewelry"}
        description={category && category !== "all" ? `Shop hand-crafted ${category} at Star Jewels — premium artificial jewelry and fashion jewellery made in India. Free shipping over ₹1,499.` : "Browse the full Star Jewels catalogue — earrings, necklaces, bracelets and rings. Premium fashion & imitation jewelry, free shipping over ₹1,499."}
        path={`/shop${category && category !== "all" ? `?category=${category}` : ""}`}
      />
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline">The Boutique</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light mt-2">
            {collection
              ? (collections.find((c) => c.id === collection)?.name || "Collection")
              : (category === "all" ? "All jewellery" : CATEGORIES.find((c) => c.key === category)?.label)}
          </h1>
          {q && <div className="mt-2 text-sm text-sj-muted">Searching for “{q}”{queryColor && <span className="ml-1 text-sj-goldDark">· sorting by color match</span>}</div>}
        </div>
        <form onSubmit={applySearch} className="flex items-center bg-sj-cream rounded-full px-4 h-11 w-full md:w-[320px]" data-testid="shop-search-form">
          <Search size={16} className="text-sj-muted" />
          <input
            data-testid="shop-search-input"
            value={searchLocal}
            onChange={(e) => setSearchLocal(e.target.value)}
            placeholder="Search jewelry, e.g. 'gold hoop'..."
            className="bg-transparent flex-1 pl-2 text-sm focus:outline-none"
          />
          {searchLocal && (
            <button
              type="button"
              onClick={() => { setSearchLocal(""); const nxt = new URLSearchParams(params); nxt.delete("q"); setParams(nxt); }}
              className="p-1"
              data-testid="shop-search-clear"
            >
              <X size={14} className="text-sj-muted" />
            </button>
          )}
        </form>
      </div>

      {/* Two-column layout: sidebar (desktop) + grid */}
      <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-10">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block sticky top-24 self-start h-max" data-testid="filters-sidebar">
          <FiltersPanel
            categoriesSel={categoriesSel}
            onToggleCategory={toggleCategory}
            priceCap={priceCap}
            maxPrice={maxPrice}
            onPriceChange={setPriceCap}
            colorsSel={colorsSel}
            allColors={allColors}
            onToggleColor={toggleColor}
            availability={availability}
            onAvailabilityChange={setAvailability}
            occasionsSel={occasionsSel}
            allOccasions={occasions}
            onToggleOccasion={toggleOccasion}
            onReset={resetAll}
            swatchMap={swatchMap}
          />
        </aside>

        <div>
          {/* Toolbar */}
          <div className="flex items-center justify-between border-y border-sj-ink/10 py-3 mb-8">
            <button
              onClick={() => setShowFilters(true)}
              className="lg:hidden flex items-center gap-2 text-sm text-sj-inkSoft hover:text-sj-goldDark"
              data-testid="filters-toggle"
            >
              <SlidersHorizontal size={16} /> Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-sj-goldDark text-white text-[10px] font-semibold" data-testid="filters-count-badge">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <div className="hidden lg:block text-xs uppercase tracking-[0.2em] text-sj-inkSoft">
              {activeFilterCount > 0 ? `${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} applied` : "All jewellery"}
            </div>
            <div className="text-sm text-sj-muted" data-testid="results-count">
              {cards.length} {cards.length === 1 ? "piece" : "pieces"}
            </div>
            <label className="relative inline-flex items-center gap-1">
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-transparent text-sm border-none focus:outline-none cursor-pointer text-sj-inkSoft appearance-none pr-5"
              >
                {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-0 text-sj-inkSoft" />
            </label>
          </div>

          {loading ? (
            <StarLoader full label="Curating your pieces" />
          ) : cards.length === 0 ? (
            <div className="max-w-2xl mx-auto py-16 sm:py-20 text-center" data-testid="empty-results">
              <div className="flex items-center justify-center gap-3 mb-4">
                <MoonAvatar size={44} />
                <div className="overline">A note from Moon</div>
              </div>
              <p className="text-sj-ink font-display text-xl sm:text-2xl leading-snug italic max-w-xl mx-auto">
                "I couldn't find exactly what you're looking for. Here are a few pieces I think you'll love."
              </p>
              {recommended.length > 0 && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mt-10 text-left" data-testid="empty-recommendations">
                  {recommended.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
              )}
              <button onClick={() => { setParams({}); setSearchLocal(""); resetAll(); }} className="btn-outline mt-10" data-testid="clear-all">Clear filters</button>
            </div>
          ) : (
            <>
              {/* Moon's Picks — collection views only, ≥6 products, top 4 featured */}
              {collection && products.length >= 6 && (() => {
                const picks = products.filter((p) => p.featured).slice(0, 4);
                if (picks.length < 3) return null;
                return (
                  <section className="mb-10 sm:mb-14 pb-8 sm:pb-12 border-b border-sj-ink/10" data-testid="moons-picks">
                    <div className="flex items-center gap-3 mb-5">
                      <MoonAvatar size={32} />
                      <div>
                        <div className="overline">Moon's Picks</div>
                        <div className="font-display text-lg sm:text-xl mt-0.5">Her favourites from this collection</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                      {picks.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                    </div>
                  </section>
                );
              })()}

              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8" data-testid="products-grid">
                {cards.map((c, i) => (
                  <ProductCard
                    key={`${c.product.id}-${c.variantLabel || "default"}`}
                    product={c.product}
                    index={i}
                    variantImageIndex={c.variantImageIndex}
                    variantLabel={c.variantLabel}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile bottom-sheet filters */}
      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-[90] flex items-end" data-testid="filters-sheet">
          <div
            className="absolute inset-0 bg-sj-ink/50 backdrop-blur-sm"
            onClick={() => setShowFilters(false)}
            aria-hidden="true"
          />
          <div className="relative w-full max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white p-5 pb-8 shadow-2xl">
            <FiltersPanel
              categoriesSel={categoriesSel}
              onToggleCategory={toggleCategory}
              priceCap={priceCap}
              maxPrice={maxPrice}
              onPriceChange={setPriceCap}
              colorsSel={colorsSel}
              allColors={allColors}
              onToggleColor={toggleColor}
              availability={availability}
              onAvailabilityChange={setAvailability}
              occasionsSel={occasionsSel}
              allOccasions={occasions}
              onToggleOccasion={toggleOccasion}
              onReset={resetAll}
              onClose={() => setShowFilters(false)}
              showCloseButton
              swatchMap={swatchMap}
            />
            <button
              type="button"
              onClick={() => setShowFilters(false)}
              className="mt-6 w-full h-12 rounded-full bg-sj-ink text-white text-sm font-medium"
              data-testid="filters-apply"
            >
              Show {cards.length} {cards.length === 1 ? "piece" : "pieces"}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
